// ECS — Entity Component System
// Packed typed arrays + bitset queries. Zero allocation in hot paths.

export const MAX_ENTITIES = 4096;
export type Entity = number;
export type System = () => void;

// --- BitSet ---

export class BitSet {
  readonly words: Uint32Array;

  constructor(size = MAX_ENTITIES) {
    this.words = new Uint32Array(Math.ceil(size / 32));
  }

  set(i: number): void {
    this.words[i >>> 5] |= 1 << (i & 31);
  }

  clear(i: number): void {
    this.words[i >>> 5] &= ~(1 << (i & 31));
  }

  has(i: number): boolean {
    return (this.words[i >>> 5] & (1 << (i & 31))) !== 0;
  }
}

// --- Query ---
// Reusable result buffer — no allocation per query

const _queryBuf = new Uint16Array(MAX_ENTITIES);

export function query(...masks: BitSet[]): { ids: Uint16Array; count: number } {
  let count = 0;
  const first = masks[0];
  const len = first.words.length;

  for (let w = 0; w < len; w++) {
    let bits = first.words[w];
    for (let m = 1; m < masks.length; m++) {
      bits &= masks[m].words[w];
    }
    while (bits !== 0) {
      const lsb = bits & -bits;
      _queryBuf[count++] = (w << 5) + (31 - Math.clz32(lsb));
      bits ^= lsb;
    }
  }

  return { ids: _queryBuf, count };
}

// --- Component Stores ---

export class Vec2Store {
  x = new Int32Array(MAX_ENTITIES);
  y = new Int32Array(MAX_ENTITIES);
  mask = new BitSet();

  set(id: number, x: number, y: number): void {
    this.x[id] = x;
    this.y[id] = y;
    this.mask.set(id);
  }

  remove(id: number): void {
    this.x[id] = 0;
    this.y[id] = 0;
    this.mask.clear(id);
  }
}

export class Vec3Store {
  x = new Int32Array(MAX_ENTITIES);
  y = new Int32Array(MAX_ENTITIES);
  z = new Int32Array(MAX_ENTITIES);
  mask = new BitSet();

  set(id: number, x: number, y: number, z: number): void {
    this.x[id] = x;
    this.y[id] = y;
    this.z[id] = z;
    this.mask.set(id);
  }

  remove(id: number): void {
    this.x[id] = 0;
    this.y[id] = 0;
    this.z[id] = 0;
    this.mask.clear(id);
  }
}

export class IntStore {
  data = new Int32Array(MAX_ENTITIES);
  mask = new BitSet();

  set(id: number, value: number): void {
    this.data[id] = value;
    this.mask.set(id);
  }

  get(id: number): number {
    return this.data[id];
  }

  remove(id: number): void {
    this.data[id] = 0;
    this.mask.clear(id);
  }
}

export class TagStore {
  data: string[] = new Array(MAX_ENTITIES).fill("");
  mask = new BitSet();

  set(id: number, value: string): void {
    this.data[id] = value;
    this.mask.set(id);
  }

  get(id: number): string {
    return this.data[id];
  }

  remove(id: number): void {
    this.data[id] = "";
    this.mask.clear(id);
  }
}

// --- World ---

interface Store {
  mask: BitSet;
  remove(id: number): void;
}

export class World {
  tick = 0;
  private nextId = 0;
  alive = new BitSet();
  private stores: Store[] = [];

  private track<T extends Store>(store: T): T {
    this.stores.push(store);
    return store;
  }

  vec2(): Vec2Store { return this.track(new Vec2Store()); }
  vec3(): Vec3Store { return this.track(new Vec3Store()); }
  ints(): IntStore { return this.track(new IntStore()); }
  tags(): TagStore { return this.track(new TagStore()); }

  createEntity(): Entity {
    const id = this.nextId++;
    this.alive.set(id);
    return id;
  }

  removeEntity(id: Entity): void {
    this.alive.clear(id);
    for (const s of this.stores) {
      if (s.mask.has(id)) s.remove(id);
    }
  }
}
